/**
 * FavoritesService
 * Hier slaan we de favorieten op (in local storage ofzo)
 */

import {storageService} from './storage';
import type {Favorite, FavoritesList, ProductDisplay} from '../models';
import {productService} from './ProductService';

const FAVORITES_STORAGE_KEY = '@nebulatide:favorites';

class FavoritesService {
  /**
   * Alles ophalen wat de gebruiker leuk vond
   */
  async getFavorites(): Promise<ProductDisplay[]> {
    try {
      const favoritesJson = await storageService.getItem(FAVORITES_STORAGE_KEY);
      
      if (!favoritesJson) {
        return [];
      }

      const favorites: FavoritesList = JSON.parse(favoritesJson);
      
      // Verse data ophalen uit de database voor elke favoriet
      const productPromises = favorites.map(async (favorite) => {
        try {
          const product = await productService.getProductById(
            parseInt(favorite.productId, 10),
          );
          return product;
        } catch (error) {
          console.warn(
            `FavoritesService: Kon product ${favorite.productId} niet ophalen`,
            error,
          );
          return null;
        }
      });

      const products = await Promise.all(productPromises);
      
      // Lege dingen eruit filteren (zombies).
      // Dit kan gebeuren als een product uit de database is verwijderd maar nog wel in je favorieten staat.
      return products.filter((p): p is ProductDisplay => p !== null);
    } catch (error) {
      console.error('FavoritesService: Fout bij ophalen favorieten:', error);
      throw new Error('Kon favorieten niet ophalen');
    }
  }

  /**
   * Iets toevoegen aan favorieten
   */
  async addFavorite(productId: string): Promise<void> {
    try {
      const favoritesJson = await storageService.getItem(FAVORITES_STORAGE_KEY);
      const favorites: FavoritesList = favoritesJson
        ? JSON.parse(favoritesJson)
        : [];

      // Even checken of ie er al in staat
      const existingIndex = favorites.findIndex(
        (f) => f.productId === productId,
      );

      if (existingIndex === -1) {
        // Nieuwe toevoegen
        const newFavorite: Favorite = {
          productId,
          addedAt: Date.now(),
        };
        favorites.push(newFavorite);

        // Opslaan in local storage (JSON stringify want storage snapt alleen tekst)
        await storageService.setItem(
          FAVORITES_STORAGE_KEY,
          JSON.stringify(favorites),
        );
      }
    } catch (error) {
      console.error('FavoritesService: Fout bij toevoegen favoriet:', error);
      throw new Error('Kon favoriet niet toevoegen');
    }
  }

  /**
   * Weg ermee (verwijderen uit favorieten)
   */
  async removeFavorite(productId: string): Promise<void> {
    try {
      const favoritesJson = await storageService.getItem(FAVORITES_STORAGE_KEY);
      
      if (!favoritesJson) {
        return;
      }

      const favorites: FavoritesList = JSON.parse(favoritesJson);
      const updatedFavorites = favorites.filter(
        (f) => f.productId !== productId,
      );

      await storageService.setItem(
        FAVORITES_STORAGE_KEY,
        JSON.stringify(updatedFavorites),
      );
    } catch (error) {
      console.error('FavoritesService: Fout bij verwijderen favoriet:', error);
      throw new Error('Kon favoriet niet verwijderen');
    }
  }

  /**
   * Is dit een favorietje?
   */
  async isFavorite(productId: string): Promise<boolean> {
    try {
      const favoritesJson = await storageService.getItem(FAVORITES_STORAGE_KEY);
      
      if (!favoritesJson) {
        return false;
      }

      const favorites: FavoritesList = JSON.parse(favoritesJson);
      return favorites.some((f) => f.productId === productId);
    } catch (error) {
      console.error('FavoritesService: Fout bij checken favoriet status:', error);
      return false;
    }
  }

  /**
   * Alles wegflikkeren (gebruiken we dit wel?)
   */
  async clearAllFavorites(): Promise<void> {
    try {
      await storageService.removeItem(FAVORITES_STORAGE_KEY);
    } catch (error) {
      console.error('FavoritesService: Fout bij wissen favorieten:', error);
      throw new Error('Kon favorieten niet wissen');
    }
  }
}

// Export singleton instance
export const favoritesService = new FavoritesService();
